import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Switch, Input, message, Modal, Form,
  DatePicker, TimePicker, InputNumber, Popconfirm, Row, Col, Card, Statistic, Alert, Tooltip,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ThunderboltOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getLessons, createLesson, setLessonDone, setLessonNote, deleteLesson, generateLessons,
} from '../../api/preschools';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Ders gününün notu — satır içinde düzenlenir.
 *
 * Ders YAPILDIYSA işlenen konu, YAPILMADIYSA sebep yazılıyor; hangisi
 * olduğunu `is_done` belirliyor ve yer tutucu metin de ona göre değişiyor
 * (aynı kutuya iki farklı anlam yüklendiği için, ne yazılacağı
 * kullanıcıya açıkça söylenmeli).
 *
 * KAYIT ODAK KAYBINDA (ya da Enter'da) ve yalnızca DEĞİŞTİYSE atılıyor —
 * aksi halde kullanıcı listede gezinirken her hücre gereksiz bir istek
 * üretirdi.
 */
const NotHucresi = ({ ders, agreementId, onKaydedildi }) => {
  const mevcut = (ders.is_done ? ders.topic : ders.reason) || '';
  const [deger, setDeger] = useState(mevcut);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  /*
    ⚠ ÇİFT İSTEK KORUMASI.

    Enter'a basmak ÖNCE `onPressEnter`, hemen ardından odak kaybıyla
    `onBlur` tetikliyor. İkisini de `mevcut` ile karşılaştırmak yetmiyor:
    liste yeniden yüklenene kadar prop hâlâ ESKİ değeri taşıyor, yani
    ikinci çağrı da "değişmiş" sanıp aynı isteği tekrar gönderiyor
    (yerelde iki özdeş PATCH görüldü, 21.10.2026).

    Axios katmanındaki mükerrer koruması burada devreye girmiyor —
    o bilerek yalnızca POST'u kapsıyor (frontend/CLAUDE.md).

    Bu yüzden en son GÖNDERİLEN değer ayrıca ref'te tutuluyor.
  */
  const sonGonderilen = useRef(mevcut);

  // Liste yeniden yüklendiğinde (ör. "yapıldı" çevrildiğinde) hücre
  // sunucudaki değere dönmeli; yoksa ekranda eski metin kalır.
  useEffect(() => {
    setDeger(mevcut);
    sonGonderilen.current = mevcut;
  }, [mevcut]);

  const kaydet = async () => {
    const yeni = deger.trim();
    if (yeni === sonGonderilen.current) return;
    sonGonderilen.current = yeni;
    setKaydediliyor(true);
    try {
      await setLessonNote(agreementId, ders.id, ders.is_done
        ? { topic: yeni || null }
        : { reason: yeni || null });
      onKaydedildi();
    } catch (e) {
      // Başarısızsa hem ekranı hem "gönderildi" izini geri al, yoksa
      // kullanıcı tekrar denediğinde istek hiç gitmez.
      setDeger(mevcut);
      sonGonderilen.current = mevcut;
      message.error(apiErrorMessage(e, 'Not kaydedilemedi.'));
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <Input
      size="small"
      value={deger}
      disabled={kaydediliyor}
      placeholder={ders.is_done ? 'İşlenen konu' : 'Neden yapılmadı?'}
      onChange={(e) => setDeger(e.target.value)}
      onBlur={kaydet}
      onPressEnter={kaydet}
      status={!ders.is_done && !deger ? 'warning' : undefined}
    />
  );
};

/**
 * Gidilen günler.
 *
 * ÜCRETE ETKİSİ YOK: aylık tutar sabit ve yapılmayan ders için kesinti
 * uygulanmıyor (kullanıcı kararı). Bu işaretler faturaya değil, kurumla
 * yapılan görüşmeye dayanak. "Kesinti nerede" diye arama — bilinçli.
 *
 * TELAFİ AYRI KAYIT DEĞİL: gidilemeyen gün işaretlenir, başka güne
 * gidilirse o gün için AYRI satır açılır.
 */
const PreschoolLessonsTab = ({ agreement }) => {
  const [rows, setRows] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [araliik, setAralik] = useState(null);
  const [ekleModal, setEkleModal] = useState(false);
  const [uretModal, setUretModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [onizleme, setOnizleme] = useState(null);
  const [form] = Form.useForm();
  const [uretForm] = Form.useForm();

  const yukle = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getLessons(agreement.id, {
        from: araliik?.[0]?.format('YYYY-MM-DD'),
        to: araliik?.[1]?.format('YYYY-MM-DD'),
        per_page: 500,
      });
      setRows(data.data?.data || []);
      setOzet(data.ozet || null);
    } catch {
      message.error('Ders günleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
    // Filtre bagimliligi eksik kalirsa sayfa degisince sessizce sifirlanir.
  }, [agreement.id, araliik]);

  useEffect(() => { yukle(); }, [yukle]);

  const dersEkle = async (values) => {
    setSaving(true);
    try {
      await createLesson(agreement.id, {
        // Tarih ve saat ham dayjs olarak GONDERILMEZ - UTC'ye cevrilirken
        // tarih bir gun geriye kayar (CLAUDE.md).
        date: values.date.format('YYYY-MM-DD'),
        start_time: values.start_time ? values.start_time.format('HH:mm') : null,
        hours: values.hours,
        topic: values.topic || null,
        is_done: values.is_done ?? true,
      });
      message.success('Ders günü eklendi.');
      setEkleModal(false);
      form.resetFields();
      yukle();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Eklenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const uret = async (preview) => {
    setSaving(true);
    try {
      const values = uretForm.getFieldsValue();
      const { data } = await generateLessons(agreement.id, {
        from: values.range?.[0]?.format('YYYY-MM-DD'),
        to: values.range?.[1]?.format('YYYY-MM-DD'),
        preview,
      });

      if (preview) {
        setOnizleme(data);
      } else {
        message.success(data.message);
        setUretModal(false);
        setOnizleme(null);
        yukle();
      }
    } catch (error) {
      message.error(apiErrorMessage(error, 'Üretilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'date',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <span>{dayjs(v).format('DD.MM.YYYY')}</span>
          <span className="text-xs text-gray-400">
            {dayjs(v).format('dddd')}
            {r.start_time ? ` · ${String(r.start_time).slice(0, 5)}` : ''}
          </span>
        </Space>
      ),
    },
    { title: 'Süre', dataIndex: 'hours', width: 90, render: (v) => `${Number(v)} saat` },
    {
      title: 'Yapıldı',
      dataIndex: 'is_done',
      width: 110,
      render: (v, r) => (
        <Switch
          size="small"
          checked={v}
          checkedChildren="Evet"
          unCheckedChildren="Hayır"
          onChange={async (checked) => {
            try {
              await setLessonDone(agreement.id, r.id, { is_done: checked });
              yukle();
            } catch (error) {
              message.error(apiErrorMessage(error, 'Güncellenemedi.'));
            }
          }}
        />
      ),
    },
    {
      /*
        SÜTUN DÜZENLENEBİLİR OLDU (21.10.2026).

        Önceden salt okunur metindi: konu yalnızca ders ELLE eklenirken
        girilebiliyordu. Ders günleri toplu üretildiğinde konuyu ya da
        "neden yapılmadı" sebebini yazacak hiçbir yol yoktu — kullanıcı
        bunu "yapıldı evet olunca konu girilmiyor, hayır olunca sebep
        girilemiyor" diye bildirdi.

        Hangi alanın yazıldığı dersin durumuna bağlı: yapıldıysa `topic`,
        yapılmadıysa `reason`. Sunucu ikisini de kabul ediyor.
      */
      title: 'Sebep / İşlenen Konu',
      key: 'not',
      render: (_, r) => <NotHucresi ders={r} agreementId={agreement.id} onKaydedildi={yukle} />,
    },
    {
      /*
        HAZIR MESAJ — uygulama kimseye mesaj GÖNDERMİYOR, metni panoya
        kopyalıyor. Gönderemez de: anaokulu çocuklarının velisinin
        telefonu sistemde yok ve olmaması bilinçli bir karar. Akış:
        öğretmen kopyalar → okuldaki yetkiliye atar → yetkili velilere
        iletir.

        Metin ANAOKULUNDA duruyor (anlaşmada değil), çünkü anlaşma
        sezonluk ve mesaj her sezon baştan yazılmamalı. Tanımlı değilse
        düğme HİÇ çizilmiyor — çalışmayan düğme göstermek kötü.
      */
      title: '',
      key: 'mesaj',
      width: 44,
      render: (_, r) => {
        const sablon = agreement.preschool?.whatsapp_message;
        if (!sablon) return null;
        const metin = sablon
          .replaceAll('{konu}', r.topic || '')
          .replaceAll('{tarih}', r.date ? dayjs(r.date).format('DD.MM.YYYY') : '');
        return (
          <Tooltip title="Haftalık mesajı panoya kopyala">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(metin);
                  message.success('Mesaj kopyalandı.');
                } catch {
                  // Pano izni yoksa/eski tarayıcıda sessiz kalmasın —
                  // kullanıcı kopyalandığını sanıp boş mesaj atmasın.
                  message.error('Kopyalanamadı. Metni Anaokulu kaydından elle alabilirsiniz.');
                }
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'sil',
      width: 50,
      render: (_, r) => (
        <Popconfirm
          title="Ders kaydı silinsin mi?"
          onConfirm={async () => {
            try {
              await deleteLesson(agreement.id, r.id);
              message.success('Silindi.');
              yukle();
            } catch (error) {
              message.error(apiErrorMessage(error, 'Silinemedi.'));
            }
          }}
          okText="Sil"
          cancelText="Vazgeç"
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {ozet && (
        <Row gutter={16} className="mb-4">
          <Col xs={8}><Card size="small"><Statistic title="Toplam Gün" value={ozet.toplam} /></Card></Col>
          <Col xs={8}><Card size="small"><Statistic title="Yapılan" value={ozet.yapilan} /></Card></Col>
          <Col xs={8}><Card size="small"><Statistic title="Toplam Saat" value={ozet.saat} /></Card></Col>
        </Row>
      )}

      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <DatePicker.RangePicker
          format="DD.MM.YYYY"
          value={araliik}
          onChange={setAralik}
          allowClear
        />
        <Space wrap>
          <Button icon={<ThunderboltOutlined />} onClick={() => { setUretModal(true); setOnizleme(null); }}>
            Programdan Üret
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEkleModal(true)}>
            Gün Ekle
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'Ders günü yok. Haftalık programı tanımlayıp "Programdan Üret" diyebilirsiniz.' }}
      />

      {/* --- Gün ekle --- */}
      <Modal
        title="Ders Günü Ekle"
        open={ekleModal}
        onCancel={() => { setEkleModal(false); form.resetFields(); }}
        footer={null}
      >
        <p className="text-gray-500 text-sm mb-3">
          Telafi için ayrı bir kayıt türü yok — başka güne gidildiyse o günü
          normal bir ders günü olarak ekleyin.
        </p>
        <Form form={form} layout="vertical" onFinish={dersEkle} initialValues={{ hours: 1, is_done: true }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="date" label="Tarih" rules={[{ required: true, message: 'Tarih seçiniz.' }]}>
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="start_time" label="Saat">
                <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="hours" label="Süre (saat)" rules={[{ required: true }]}>
            <InputNumber min={0.5} max={12} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="topic" label="İşlenen Konu">
            <Input placeholder="Bu hafta ne yapıldı" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>Kaydet</Button>
        </Form>
      </Modal>

      {/* --- Programdan üret --- */}
      <Modal
        title="Programdan Ders Günü Üret"
        open={uretModal}
        onCancel={() => { setUretModal(false); setOnizleme(null); }}
        footer={null}
      >
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="Var olan günler atlanır"
          description="Üretim asla silmez, yalnızca eksik günleri ekler. Tekrar çalıştırmak güvenlidir; elle eklediğiniz günler korunur."
        />
        <Form form={uretForm} layout="vertical">
          <Form.Item name="range" label="Aralık (boşsa anlaşmanın tamamı)">
            <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>

        {onizleme && (
          <Alert
            type={onizleme.eklenecek > 0 ? 'success' : 'warning'}
            className="mb-3"
            message={`${onizleme.eklenecek} gün eklenecek`}
            description={onizleme.eklenecek > 0
              ? onizleme.ornekler.map((o) => dayjs(o.date).format('DD.MM.YYYY')).join(', ') + (onizleme.eklenecek > 10 ? ' …' : '')
              : 'Bu aralıktaki tüm günler zaten tanımlı.'}
          />
        )}

        <Space>
          <Button onClick={() => uret(true)} loading={saving}>Önizle</Button>
          <Button type="primary" onClick={() => uret(false)} loading={saving}>Üret</Button>
        </Space>
      </Modal>
    </div>
  );
};

export default PreschoolLessonsTab;
